import { createReducer, on } from '@ngrx/store';
import { ActionReducer } from '@ngrx/store';
import {
  HomepageRisksDome,
  SystemSecurityScore,
  Task,
  TaskDetails,
} from 'projects/shared/src/lib/core/interfaces';
import {
  addHomeTaskComment_Success,
  fetchHomeRisksDome,
  fetchHomeRisksDome_Failure,
  fetchHomeRisksDome_Success,
  fetchHomeSystemSecurityScore,
  fetchHomeSystemSecurityScoreFailure,
  fetchHomeSystemSecurityScoreSuccess,
  fetchHomeTaskDetails,
  fetchHomeTaskDetails_Failure,
  fetchHomeTaskDetails_Success,
  fetchHomeTasks,
  fetchHomeTasks_Failure,
  fetchHomeTasks_Success,
  removeHomeTaskDetailsSidebar,
  resetHomeTasksStore,
  setHomeTaskDetailsCommentFailed,
  setHomeTaskDetailsCommentPending,
  toggleHomeTaskSidebar,
} from './home.actions';

export const homeFeatureKey = 'homeFeatureKey';
export type HomeState = {
  systemSecurityScoreIsLoading?: boolean;
  systemSecurityScore?: SystemSecurityScore;
  tasks?: Task[];
  tasksIsLoading?: boolean;
  showTaskSidebar?: boolean;
  taskDetails?: TaskDetails;
  taskDetailsLoading?: boolean;
  selectedTaskId?: string;
  risksDome?: HomepageRisksDome;
  risksDomeIsLoading?: boolean;
};

export const initialState: HomeState = {};

export const homeReducer: ActionReducer<HomeState> = createReducer(
  initialState,
  on(fetchHomeSystemSecurityScore, (state, { bypassLoader }) => ({
    ...state,
    systemSecurityScoreIsLoading: !bypassLoader,
  })),
  on(fetchHomeSystemSecurityScoreSuccess, (state, { systemSecurityScore }) => ({
    ...state,
    systemSecurityScore,
    systemSecurityScoreIsLoading: false,
  })),
  on(fetchHomeSystemSecurityScoreFailure, (state) => ({
    ...state,
    systemSecurityScoreIsLoading: false,
  })),
  on(fetchHomeRisksDome, (state) => ({
    ...state,
    risksDomeIsLoading: true,
  })),
  on(fetchHomeRisksDome_Success, (state, { risksDome }) => ({
    ...state,
    risksDome,
    risksDomeIsLoading: false,
  })),
  on(fetchHomeRisksDome_Failure, (state) => ({
    ...state,
    risksDomeIsLoading: false,
  })),
  on(fetchHomeTasks, (state, { bypassLoader }) => ({
    ...state,
    tasksIsLoading: !bypassLoader,
  })),
  on(fetchHomeTasks_Success, (state, { tasks }) => ({
    ...state,
    tasks,
    tasksIsLoading: false,
  })),
  on(fetchHomeTasks_Failure, (state) => ({
    ...state,
    tasksIsLoading: false,
  })),
  on(fetchHomeTaskDetails, (state, { taskId, bypassLoader }) => ({
    ...state,
    selectedTaskId: taskId,
    taskDetailsLoading: !bypassLoader,
  })),
  on(fetchHomeTaskDetails_Success, (state, { taskDetails }) => ({
    ...state,
    taskDetails,
    taskDetailsLoading: false,
  })),
  on(fetchHomeTaskDetails_Failure, (state) => ({
    ...state,
    taskDetailsLoading: false,
  })),
  on(removeHomeTaskDetailsSidebar, (state) => ({
    ...state,
    taskDetails: undefined,
    selectedTaskId: undefined,
  })),
  on(toggleHomeTaskSidebar, (state, { showSidebar }) => ({
    ...state,
    showTaskSidebar: showSidebar,
  })),
  on(addHomeTaskComment_Success, (state, { comment, taskId }) => {
    if (taskId !== state.selectedTaskId) {
      return state;
    }
    const comments = [...(state.taskDetails?.comments || [])];
    comments.unshift(comment);
    return {
      ...state,
      taskDetails: {
        ...state.taskDetails,
        comments,
        newComment: comment,
      } as TaskDetails,
    };
  }),
  on(setHomeTaskDetailsCommentPending, (state, { pending, taskId }) => {
    if (taskId !== state.selectedTaskId) {
      return state;
    }
    return {
      ...state,
      taskDetails: {
        ...state.taskDetails,
        taskDetailsCommentPending: pending,
      } as TaskDetails,
    };
  }),
  on(setHomeTaskDetailsCommentFailed, (state, { failed, errorMessage, taskId }) => {
    if (taskId !== state.selectedTaskId) {
      return state;
    }
    return {
      ...state,
      taskDetails: {
        ...state.taskDetails,
        taskDetailsCommentFailed: failed,
        commentErrorMessage: failed ? errorMessage : undefined,
      } as TaskDetails,
    };
  }),
  on(resetHomeTasksStore, (state): HomeState => ({
    ...state,
    tasks: undefined,
    tasksIsLoading: undefined,
    showTaskSidebar: undefined,
    taskDetails: undefined,
    taskDetailsLoading: undefined,
    selectedTaskId: undefined,
  })),
);
